/**
 * GLAMRDiP Kit BundleBuilder — fixed-tier packages with click-to-pin.
 * 2026-04-28 — replaces the progressive % OFF model.
 *
 * Mechanic:
 *   - Tile click → opens in-place detail pane (or directly toggles into
 *     state.selected if quickview disabled).
 *   - Active tier auto-derives: lowest tier whose slot_count ≥
 *     state.selected.length (defaults to first tier when empty).
 *   - User can CLICK any tier circle to PIN it (state.pinnedTierIdx).
 *     Pin wins until selection overflows pinned.slot_count → pin clears,
 *     auto-derive resumes (auto-bump).
 *   - Slot × splices state.selected; if pinned tier no longer needed
 *     after a removal, pin stays (we only auto-bump on add overflow,
 *     not on remove underflow).
 *   - Stepper visual driven by render(): .is-active on the active tier;
 *     .is-reached on every tier whose slot_count ≤ active.slot_count.
 *   - ATC → /cart/add.js POST mirroring instant-gels-builder.js shape.
 *
 * Cart payload (fixed-tier):
 *   items: [
 *     { id: activeTier.variant_id, qty: 1, properties: {
 *         Tier:         activeTier.title,
 *         Colours:      "Pillow Talk,Milky Way,Sahara",
 *         __bundle_id:  <uuid>,
 *         __slot_count: "4",
 *     }},
 *     ...selected.map(c => ({
 *       id: c.variantId, qty: 1, properties: {
 *         __bundle_id: <uuid>, _hidden: "true"
 *       }
 *     }))
 *   ]
 *
 * Preserved infrastructure:
 *   - window.GlamrDipKit.open() / .close() programmatic API
 *   - ESC + overlay close (commit bc2e91a)
 *   - Body scroll lock on modal open
 *   - 12 chips no-All, last-active-chip guard (commit 0459f69)
 *   - Cross-page guard (only fires when kit modal is in DOM and IG modal isn't)
 *   - In-place detail pane (replaces layered popup; commit 6798ede)
 *
 * Analytics events: kit_modal_open, kit_color_added, kit_color_removed,
 * kit_quickview_open, kit_filter_toggled, kit_tier_pinned, kit_atc_click,
 * kit_atc_success, kit_atc_error.
 */
(() => {
  const roots = document.querySelectorAll('[data-gd-kit-builder]');
  if (!roots.length) return;
  roots.forEach(hydrate);

  function hydrate(root) {
    const productId = root.dataset.productId;
    const handle    = root.dataset.productHandle;
    const defaultVariantId = parseInt(root.dataset.defaultVariantId, 10);

    const modal = root.closest('[data-gd-kit-builder-modal]');

    // Tier config — full details baked by Liquid (variant_id, prices, slots).
    const tierConfig = safeJson(root.querySelector('[data-gd-kit-tier-config]')?.textContent, []);

    // Element refs
    const tiles            = [...root.querySelectorAll('[data-gd-kit-tile]')];
    const chips            = [...root.querySelectorAll('[data-gd-kit-chip]')];
    const chipsScroll      = root.querySelector('[data-gd-kit-chips]');
    const chipsPrev        = root.querySelector('[data-gd-kit-chips-prev]');
    const chipsNext        = root.querySelector('[data-gd-kit-chips-next]');
    const slotsHosts       = [...root.querySelectorAll('[data-gd-kit-slots], [data-gd-kit-mobile-slots]')];
    const upsellEl         = root.querySelector('[data-gd-kit-upsell]');
    const statusEl         = root.querySelector('[data-gd-kit-status]');
    const noticeEl         = root.querySelector('[data-gd-kit-notice]');
    const priceEls         = [...root.querySelectorAll('[data-gd-kit-price], [data-gd-kit-mobile-price]')];
    const priceBlockEls    = [...root.querySelectorAll('[data-gd-price-block]')];
    let _lastChipSlotCount = -1;  // skip pop-animation on the first paint
    const wasEls           = [...root.querySelectorAll('[data-gd-kit-was], [data-gd-kit-mobile-was]')];
    const savingsEl        = root.querySelector('[data-gd-kit-savings]');
    const progressFillEl   = root.querySelector('[data-gd-kit-progress-fill]');
    const atcButtons       = [...root.querySelectorAll('[data-gd-kit-atc]')];
    const atcTextEls       = [...root.querySelectorAll('[data-gd-kit-atc-text]')];
    const errorEl          = root.querySelector('[data-gd-kit-error]');

    // Fixed-tier model (per Joel 2026-04-28). tierConfig is sorted ascending
    // by slot_count (Liquid sorted on emit). Each tier carries variant_id,
    // title, slot_count, price_cents, label_count.
    const sortedTiers = [...tierConfig].sort((a, b) => (a.slot_count|0) - (b.slot_count|0));
    const topSlotCount = sortedTiers.length ? (sortedTiers[sortedTiers.length - 1].slot_count|0) : 0;

    const state = {
      selected:       [],
      chips:          new Set([0]),
      pinnedTierIdx:  null,   // null = auto-derive from count
    };

    function getActiveTier() {
      if (sortedTiers.length === 0) return null;
      // Pinned wins when set and within scope (pin can be set even when the
      // tier currently has spare slots; auto-bump only fires on overflow).
      if (state.pinnedTierIdx !== null && sortedTiers[state.pinnedTierIdx]) {
        return sortedTiers[state.pinnedTierIdx];
      }
      // Auto-derive: lowest tier whose slot_count ≥ selected.length. Empty
      // selection defaults to the smallest package so the price reads from
      // the first tier on modal open.
      const sel = state.selected.length;
      if (sel === 0) return sortedTiers[0];
      for (const t of sortedTiers) {
        if ((t.slot_count|0) >= sel) return t;
      }
      // Beyond the largest tier — sit at the top tier.
      return sortedTiers[sortedTiers.length - 1];
    }

    // Clears the pin if the pinned tier can no longer hold the current
    // selection (called after every add). Removes are not auto-bumped down.
    function maybeClearPinOnOverflow() {
      if (state.pinnedTierIdx === null) return;
      const pinned = sortedTiers[state.pinnedTierIdx];
      if (!pinned || (pinned.slot_count|0) < state.selected.length) {
        state.pinnedTierIdx = null;
      }
    }

    // ---- Modal open/close ---------------------------------------------------
    const openModal = () => {
      if (!modal) return;
      modal.hidden = false;
      // Joel 2026-04-30: use shared iOS-safe scroll-lock. Without this,
      // bundle modal lets the PDP page scroll behind it on iOS Safari
      // when the user reaches the modal's scroll boundary.
      if (window.gdScrollLock) window.gdScrollLock.lock(scrollRegion || modal);
      else document.body.style.overflow = 'hidden';
      // Always start in grid pane regardless of how the modal was last
      // closed (e.g. user × out of detail view → reopen should land on grid).
      if (detailPane) detailPane.hidden = true;
      if (gridPane)   gridPane.hidden   = false;
      root.classList.remove('is-quickview');
      qvState = { tile: null, images: [], idx: 0 };
      // Joel 2026-05-04: each session opens with the MIDDLE tier (4 Colours)
      // pinned instead of auto-deriving to first tier (2 Colours). Anchors
      // pricing higher and makes the upgrade visible from first paint of
      // the popup. activeTier() honours state.pinnedTierIdx ahead of the
      // count-derived value, so the visual checkmark + price sidebar both
      // reflect tier index 1. User can click any tier dot to change.
      // maybeClearPinOnOverflow() auto-clears the pin if user adds more
      // colours than the pinned tier holds — e.g. pin=1 (4 cols), user
      // adds 5th colour → pin clears, tier auto-bumps to index 2 (6 cols).
      // Defensive: only pin tier 1 when there are at least 2 tiers; with a
      // single-tier setup we fall back to null (auto-derive).
      state.pinnedTierIdx = sortedTiers && sortedTiers.length >= 2 ? 1 : null;
      modal.querySelector('[data-close]')?.focus?.();
      // Joel 2026-04-29: dispatch an open event so layout-dependent
      // sub-components (e.g. chip-arrow visibility) can recompute now
      // that the modal is no longer display:none. setTimeout(0) yields
      // to the browser so layout settles before measurement runs.
      document.dispatchEvent(new CustomEvent('gd-kit-modal:opened'));
      setTimeout(() => document.dispatchEvent(new CustomEvent('gd-kit-modal:opened')), 50);
      emit('kit_modal_open', { product_id: productId });
      render();
    };
    const closeModal = () => {
      if (!modal) return;
      modal.hidden = true;
      if (window.gdScrollLock) window.gdScrollLock.unlock(scrollRegion || modal);
      else document.body.style.overflow = '';
    };
    window.GlamrDipKit = Object.assign(window.GlamrDipKit || {}, {
      open:  openModal,
      close: closeModal,
    });

    // Cross-page guard: only act when this page hosts the kit modal AND
    // the IG modal isn't also in DOM. On Salon Kit PDP only kit modal
    // is rendered; on IG PDP only IG modal is rendered.
    if (modal && !document.__gdKitTriggerBound) {
      document.__gdKitTriggerBound = true;
      document.addEventListener('click', (e) => {
        const trigger = e.target.closest('[data-gd-kit-builder-trigger], [data-gd-open-bundle-builder]');
        if (!trigger) return;
        const hasKitModal = document.querySelector('[data-gd-kit-builder-modal]');
        const hasInstantModal = document.querySelector('[data-gd-instant-modal]');
        if (!hasKitModal || hasInstantModal) return;
        e.preventDefault();
        openModal();
      });

      // Overlay + [data-close] elements close
      modal.addEventListener('click', (e) => {
        if (e.target.closest('[data-close]')) {
          e.preventDefault();
          closeModal();
        }
      });

      // ESC handler: if detail pane is open, close it first (return to
      // grid). Only close the modal if the detail pane is already shut.
      document.addEventListener('keydown', (e) => {
        if (e.key !== 'Escape' || modal.hidden) return;
        if (detailPane && !detailPane.hidden) { closeQuickView(); return; }
        closeModal();
      });
    }

    // (Legacy gd:kit-tier-change listener + applyActiveTier removed —
    // count-driven model derives activeTier on every render.)

    // ---- Tile click — branch on quickview-enabled flag ----------------------
    // When enabled (default): click opens quick-view popup; user confirms via
    // ADD TO BOX inside the popup. When disabled: legacy IG direct-toggle.
    const quickviewEnabled = root.dataset.quickviewEnabled !== 'false';
    tiles.forEach((tile) => {
      tile.addEventListener('click', (e) => {
        e.preventDefault();
        if (quickviewEnabled) {
          openQuickView(tile);
          return;
        }
        toggleTileSelection(tile);
      });
    });

    // Tracks the slot index that was JUST filled by the most recent
    // toggleTileSelection — render() reads this to add .is-just-filled
    // for one animation frame, then clears it so the pop only plays once.
    let _justFilledSlotIdx = -1;

    // Joel 2026-05-02 (OOS pre-validation):
    // The server-side {%- unless pp.available -%}{%- continue -%}{%- endunless -%}
    // filter in kit-builder.liquid catches the cheap case where Storefront API
    // reports the kit-colour duplicate as available:false at render time. It
    // does NOT catch:
    //   1. Stale Liquid cache (Shopify caches PDP renders ~1-5min; inventory
    //      can flip OOS in that window).
    //   2. Shared-SKU + shared-barcode duplicates (e.g. awakening-bundle
    //      shares SKU POW-AWA-050 + barcode 769807637016 with regular
    //      awakening). Their inventory pools desync between Storefront API's
    //      `available` field and cart-add's live inventory check.
    //
    // Fix B+C dual-fetch:
    //   - Fix B: query duplicate via /products/<handle>.js. Catches simple
    //     stale-cache case.
    //   - Fix C: also query the PAIRED regular product (handle minus -bundle
    //     suffix). Regular has inventory_management:'shopify'; duplicate has
    //     inventory_management:null — the regular's `available` flag reflects
    //     the shared-SKU pool. Cart-add rejects the duplicate when this pool
    //     is depleted, so blocking on regular.available is the only reliable
    //     signal pre-cart-add.
    //   - Cross-check shared SKU: if the regular has a variant with the same
    //     SKU as the duplicate's variant and that variant is unavailable,
    //     block. Catches multi-variant regulars where only one SKU is OOS.
    // Fail-open: any network/parse error defers to existing cart-add error
    // fallback. Both fetches in parallel via Promise.all.
    async function preValidateTile(tile) {
      const handle = tile.dataset.productHandle;
      const variantId = String(tile.dataset.variantId || '');
      if (!handle || !variantId) return true;

      // Convention: kit-colour duplicates are <handle>-bundle. Regular
      // product = strip -bundle suffix. If handle doesn't end in -bundle,
      // there's no paired regular to check (fail-open via the equality test
      // in the Promise.all below).
      const regularHandle = handle.endsWith('-bundle')
        ? handle.slice(0, -'-bundle'.length)
        : handle;

      const fetchProduct = async (h) => {
        try {
          const resp = await fetch(`/products/${h}.js?_=${Date.now()}`, {
            credentials: 'same-origin',
            headers: { 'Cache-Control': 'no-cache', Accept: 'application/json' },
          });
          if (!resp.ok) return null;
          return await resp.json();
        } catch (e) {
          return null;
        }
      };

      try {
        const [duplicate, regular] = await Promise.all([
          fetchProduct(handle),
          regularHandle !== handle ? fetchProduct(regularHandle) : Promise.resolve(null),
        ]);

        // Fix B: duplicate's own variant.available — fast path for simple OOS.
        if (duplicate) {
          const dupVariant = (duplicate.variants || []).find((v) => String(v.id) === variantId);
          if (dupVariant && dupVariant.available === false) return false;
        }

        // Fix C: paired regular product overall availability. If the regular's
        // entire product.available is false, the shared-SKU pool is depleted
        // and cart-add will reject the duplicate.
        if (regular && regular.available === false) {
          console.info('[gd-kit-builder] OOS via paired regular:', regularHandle);
          return false;
        }

        // Fix C cross-check: shared-SKU variant on the regular. Multi-variant
        // regulars may have one SKU OOS while overall product.available stays
        // true (other variants in stock). Match by SKU and block on that.
        if (regular && duplicate) {
          const dupVariant = (duplicate.variants || []).find((v) => String(v.id) === variantId);
          const dupSku = dupVariant && dupVariant.sku;
          if (dupSku) {
            const matchingRegular = (regular.variants || []).find((v) => v.sku === dupSku);
            if (matchingRegular && matchingRegular.available === false) {
              console.info('[gd-kit-builder] OOS via shared-SKU regular variant:', dupSku);
              return false;
            }
          }
        }

        return true;
      } catch (e) {
        console.warn('[gd-kit-builder] OOS pre-check failed, deferring to cart-add:', e && e.message);
        return true;
      }
    }

    // Joel 2026-05-02 (optimistic-with-undo): toggleTileSelection no longer
    // awaits preValidateTile. The dual-fetch (~150-300ms) was causing
    // perceptible click delay, especially compounded with the qv close
    // animation. Now: ADD pushes to state.selected immediately and renders;
    // preValidateTile fires in the background; if it resolves false, the
    // selection is silently undone, the tile is marked OOS, and a transient
    // toast surfaces via [data-gd-kit-notice]. The 3-layer defense remains:
    // server-side {%- unless pp.available -%} + this JS validation +
    // cart-add error fallback for race conditions.
    function showOosNotice(name) {
      const el = root && root.querySelector('[data-gd-kit-notice]');
      if (!el) return;
      el.hidden = false;
      el.textContent = `${name || 'That colour'} just sold out — pick another.`;
      el.classList.add('gd-kb__notice--oos');
      if (el._oosTimer) clearTimeout(el._oosTimer);
      el._oosTimer = setTimeout(() => {
        el.hidden = true;
        el.classList.remove('gd-kb__notice--oos');
        el._oosTimer = null;
      }, 4000);
    }

    function toggleTileSelection(tile) {
      const variantId = parseInt(tile.dataset.variantId, 10);
      const existingIdx = state.selected.findIndex(c => c.variantId === variantId);

      if (existingIdx >= 0) {
        // Removing an already-selected colour — no OOS pre-check needed.
        state.selected.splice(existingIdx, 1)[0];
        emit('kit_color_removed', { variant_id: variantId, slot: existingIdx });
        render();
        return true;
      }

      // Hard cap at the largest tier's slot_count. The fixed-tier model
      // doesn't sell more colours than the top package holds. Sync check
      // before the optimistic push so we don't briefly overflow.
      if (topSlotCount > 0 && state.selected.length >= topSlotCount) {
        tile.classList.add('is-shake');
        setTimeout(() => tile.classList.remove('is-shake'), 400);
        return false;
      }

      // Optimistic add — push immediately and render.
      state.selected.push({
        variantId,
        productId:    tile.dataset.productId,
        name:         tile.dataset.productTitle,
        image:        tile.dataset.imageUrl || '',
        swatch:       tile.dataset.swatch || '',
        finish:       tile.dataset.finish || '',
        description:  tile.dataset.description || '',
        priceCents:   parseInt(tile.dataset.priceCents, 10) || 0,
        groupIdx:     parseInt(tile.dataset.groupIndex, 10),
      });
      _justFilledSlotIdx = state.selected.length - 1;
      emit('kit_color_added', { variant_id: variantId, slot: state.selected.length - 1 });
      maybeClearPinOnOverflow();
      render();

      // Background validation. If OOS comes back, silently undo.
      preValidateTile(tile).then((ok) => {
        if (ok) return;
        const idx = state.selected.findIndex((c) => c.variantId === variantId);
        if (idx >= 0) state.selected.splice(idx, 1);
        tile.classList.add('gd-kb__tile--oos');
        tile.setAttribute('aria-disabled', 'true');
        emit('kit_color_oos_blocked', { variant_id: variantId, product_id: tile.dataset.productId });
        showOosNotice(tile.dataset.productTitle);
        render();
      });

      return true;
    }

    // ---- In-place colour detail (pane swap, NOT a layered popup) ───────────
    // Two stacked panes inside .gd-kb__scroll-region. openQuickView toggles
    // detailPane visible / gridPane hidden; closeQuickView reverses. ADD TO
    // BOX confirm + back button + ESC all return to the grid pane.
    const gridPane      = root.querySelector('[data-gd-kit-pane="grid"]');
    const detailPane    = root.querySelector('[data-gd-kit-pane="detail"]');
    const scrollRegion  = root.querySelector('.gd-kb__scroll-region');
    const qvTrack       = root.querySelector('[data-gd-qv-track]');
    const qvCounter     = root.querySelector('[data-gd-qv-counter]');
    const qvName        = root.querySelector('[data-gd-qv-name]');
    const qvFinish      = root.querySelector('[data-gd-qv-finish]');
    const qvCta         = root.querySelector('[data-gd-qv-add]');
    const qvCtaText     = root.querySelector('[data-gd-qv-cta-text]');
    const qvPrev        = root.querySelector('[data-gd-qv-prev]');
    const qvNext        = root.querySelector('[data-gd-qv-next]');
    const qvStage       = root.querySelector('[data-gd-qv-stage]');
    const qvCloseEls    = root.querySelectorAll('[data-qv-close]');
    /* Joel 2026-04-29: quickview now scoped to the active shade's own
       gallery — was building one slide per visible TILE (one image per
       shade across the whole catalogue), so swipe cycled to the next
       shade. New shape: slideUrls = the active shade's image array;
       rebuilt on every openQuickView call. Counter reads "X / N"
       where N = that shade's image count. Arrows + counter hide when
       N <= 1. */
    let qvState = { tile: null, slideIdx: 0, slideUrls: [] };
    let _qvScrollTimer = null;

    // ── Peek-next detail carousel (Joel 2026-04-29) ─────────────────────
    // All currently-visible tiles render as slides in a snap track. Click
    // Build the track from the active shade's image gallery only.
    // `data-images` is a pipe-separated list of image URLs for THIS
    // shade (Liquid emits up to 8 from product.images). Rebuilt on
    // every openQuickView so re-entering a different shade refreshes
    // the slide set. Native horizontal scroll handles swipe; arrows
    // are programmatic scrollBy.
    function buildQuickviewTrack(tile) {
      if (!qvTrack || !tile) return;
      const imagesAttr = (tile.dataset.images || '').trim();
      const urls = imagesAttr ? imagesAttr.split('|').map(s => s.trim()).filter(Boolean) : [];
      if (urls.length === 0 && tile.dataset.imageUrl) urls.push(tile.dataset.imageUrl);
      qvState.slideUrls = urls;
      qvTrack.innerHTML = '';
      if (urls.length === 0) return;
      const altBase = tile.dataset.productTitle || '';
      const frag = document.createDocumentFragment();
      urls.forEach((url, i) => {
        const slide = document.createElement('div');
        slide.className = 'gd-kb-quickview__slide';
        slide.dataset.gdQvSlide = String(i);
        const img = document.createElement('img');
        img.className = 'gd-kb-quickview__slide-img';
        img.loading = 'eager';
        img.decoding = 'async';
        img.fetchPriority = 'high';
        img.alt = altBase ? `${altBase} — image ${i + 1} of ${urls.length}` : '';
        img.src = url;
        slide.appendChild(img);
        frag.appendChild(slide);
      });
      qvTrack.appendChild(frag);
    }

    function scrollTrackToSlide(idx, behavior) {
      if (!qvTrack) return;
      const slides = qvTrack.querySelectorAll('[data-gd-qv-slide]');
      const slide = slides[idx];
      if (!slide) return;
      // Slide is left-snap-aligned; scroll so its left edge sits at the
      // track's scroll-padding-left (the peek of the previous slide).
      const targetLeft = slide.offsetLeft - parseFloat(getComputedStyle(qvTrack).scrollPaddingLeft || '0');
      qvTrack.scrollTo({ left: Math.max(0, targetLeft), behavior: behavior || 'auto' });
    }

    function updateQuickviewMetaForTile(tile) {
      qvState.tile = tile;
      if (qvName)   qvName.textContent   = tile?.dataset?.productTitle || '';
      if (qvFinish) qvFinish.textContent = tile?.dataset?.finish || '';
      if (qvCta && qvCtaText && tile) {
        const variantId = parseInt(tile.dataset.variantId, 10);
        const inSelection = state.selected.some(c => c.variantId === variantId);
        const tierFull = topSlotCount > 0 && state.selected.length >= topSlotCount && !inSelection;
        qvCta.classList.toggle('is-remove', inSelection);
        qvCta.classList.toggle('is-disabled', tierFull);
        qvCta.disabled = tierFull;
        if (inSelection)    qvCtaText.textContent = 'Remove from box';
        else if (tierFull)  qvCtaText.textContent = 'Box full';
        else                qvCtaText.textContent = 'Add to box';
      }
      const n = qvState.slideUrls.length;
      if (qvCounter) {
        if (n > 1) {
          qvCounter.textContent = `${qvState.slideIdx + 1} / ${n}`;
          qvCounter.hidden = false;
        } else {
          qvCounter.textContent = '';
          qvCounter.hidden = true;
        }
      }
      // Hide nav arrows entirely when only one image — clean single-image preview.
      if (qvPrev) qvPrev.hidden = n <= 1;
      if (qvNext) qvNext.hidden = n <= 1;
      updateArrowState();
    }

    function updateArrowState() {
      const n = qvState.slideUrls.length;
      // Joel 2026-05-01: at scroll boundaries, hide the chevron via the
      // [hidden] attribute (which the CSS resolves to display:none)
      // rather than disabling with opacity:0.3 + pointer-events:none.
      // The translucent ghost button read as "broken" instead of
      // "intentionally unavailable" — clean display:none is the
      // unambiguous affordance.
      if (qvPrev) {
        qvPrev.hidden = (n <= 1) || (qvState.slideIdx <= 0);
        qvPrev.disabled = qvPrev.hidden;
      }
      if (qvNext) {
        qvNext.hidden = (n <= 1) || (qvState.slideIdx >= n - 1);
        qvNext.disabled = qvNext.hidden;
      }
    }

    function openQuickView(tile) {
      if (!detailPane) { toggleTileSelection(tile); return; }
      buildQuickviewTrack(tile);
      qvState.slideIdx = 0;  // always land on the shade's hero image
      updateQuickviewMetaForTile(tile);
      // Pane swap before scrollTo so layout calculations are correct.
      if (gridPane) gridPane.hidden = true;
      detailPane.hidden = false;
      // Joel 2026-04-30: is-quickview on the panel root drives mobile-only
      // CSS that hides the chrome (tier cards + chips) above the scroll
      // region, freeing vertical + horizontal space so the active shade's
      // gallery owns the modal in preview mode. closeQuickView reverses.
      root.classList.add('is-quickview');
      if (scrollRegion) scrollRegion.scrollTop = 0;
      // requestAnimationFrame → wait for layout, then snap (no animation
      // on initial open; smooth scrolling kicks in for subsequent navs).
      requestAnimationFrame(() => scrollTrackToSlide(qvState.slideIdx, 'auto'));
      detailPane.querySelector('[data-qv-close]')?.focus?.();
      emit('kit_quickview_open', { variant_id: tile.dataset.variantId, slide_count: qvState.slideUrls.length });
    }

    function closeQuickView() {
      if (!detailPane) return;
      detailPane.hidden = true;
      if (gridPane) gridPane.hidden = false;
      root.classList.remove('is-quickview');
      qvState = { tile: null, slideIdx: 0, slideUrls: [] };
    }

    // Track scroll handler — update the slide index + counter as the
    // user swipes through the active shade's gallery. No tile lookup
    // anymore (slides ARE the active shade's images, not other tiles).
    qvTrack?.addEventListener('scroll', () => {
      if (_qvScrollTimer) clearTimeout(_qvScrollTimer);
      _qvScrollTimer = setTimeout(() => {
        if (!qvTrack || qvState.slideUrls.length === 0) return;
        const trackRect = qvTrack.getBoundingClientRect();
        const trackCenter = trackRect.left + trackRect.width / 2;
        let bestIdx = 0;
        let bestDist = Infinity;
        const slides = qvTrack.querySelectorAll('[data-gd-qv-slide]');
        slides.forEach((s, i) => {
          const r = s.getBoundingClientRect();
          const c = r.left + r.width / 2;
          const d = Math.abs(c - trackCenter);
          if (d < bestDist) { bestDist = d; bestIdx = i; }
        });
        if (bestIdx !== qvState.slideIdx) {
          qvState.slideIdx = bestIdx;
          // Active tile (qvState.tile) doesn't change — same shade.
          // Only the slide counter + arrow disabled-state need refresh.
          if (qvCounter && qvState.slideUrls.length > 1) {
            qvCounter.textContent = `${qvState.slideIdx + 1} / ${qvState.slideUrls.length}`;
          }
          updateArrowState();
        }
      }, 80);
    }, { passive: true });

    qvPrev?.addEventListener('click', (e) => {
      e.preventDefault();
      const next = Math.max(0, qvState.slideIdx - 1);
      qvState.slideIdx = next;
      scrollTrackToSlide(next, 'smooth');
      // Joel 2026-05-01: refresh arrow boundary state immediately so
      // the prev chevron hides at idx=0 / next chevron hides at last
      // idx without waiting for the scroll handler. The scroll
      // handler's `if (bestIdx !== qvState.slideIdx)` early-return
      // skips arrow refresh when slideIdx was already updated by
      // the click handler.
      updateArrowState();
    });
    qvNext?.addEventListener('click', (e) => {
      e.preventDefault();
      const next = Math.min(qvState.slideUrls.length - 1, qvState.slideIdx + 1);
      qvState.slideIdx = next;
      scrollTrackToSlide(next, 'smooth');
      updateArrowState();
    });

    // ADD TO BOX uses the CURRENTLY centred tile (qvState.tile is updated
    // on every scroll-settle), not the originally-clicked one.
    qvCta?.addEventListener('click', (e) => {
      e.preventDefault();
      if (!qvState.tile || qvCta.disabled) return;
      // toggleTileSelection now returns true synchronously on the optimistic
      // ADD path; background preValidateTile handles silent undo if OOS.
      const ok = toggleTileSelection(qvState.tile);
      if (ok !== false) closeQuickView();
    });

    qvCloseEls.forEach((el) => el.addEventListener('click', (e) => {
      e.preventDefault();
      closeQuickView();
    }));

    // Joel 2026-04-30: state-aware header back button.
    // Default state → closeModal (acts as cancel/exit, mirrored on the
    // left for thumb reach). Quickview state → closeQuickView (returns
    // to swatch grid, replacing the removed inline "Back to colours"
    // link). aria-label flips in lockstep so screen readers announce
    // the right action for the current state.
    const headerBackBtn = root.querySelector('[data-gd-kit-back]');
    if (headerBackBtn) {
      headerBackBtn.addEventListener('click', (e) => {
        e.preventDefault();
        // Joel 2026-04-30: permanent observability so the next time
        // someone reports "back arrow not working" we can confirm in
        // DevTools whether the click handler fired. If this log shows
        // but the user stays on the preview screen, the issue is in
        // closeQuickView's state mutation; if no log, the handler
        // isn't bound (selector mismatch / element re-rendered after
        // bind).
        const inQv = root.classList.contains('is-quickview');
        console.log('[bundle-builder] header back-arrow clicked, mode=' + (inQv ? 'quickview→returning to swatch grid' : 'default→closing modal'));
        if (inQv) {
          closeQuickView();
          headerBackBtn.setAttribute('aria-label', 'Back');
        } else {
          closeModal();
        }
      });
    }
    // Joel 2026-04-30: explicit BACK TO COLOURS secondary CTA below the
    // primary ADD/REMOVE button. Same destination as the header back
    // arrow — closeQuickView returns to the swatch grid. Symmetric
    // treatment across both selection states (the button is rendered
    // outside the ADD/REMOVE conditional in the template).
    const qvBackToColoursBtn = root.querySelector('[data-gd-qv-back-to-colours]');
    if (qvBackToColoursBtn) {
      qvBackToColoursBtn.addEventListener('click', (e) => {
        e.preventDefault();
        closeQuickView();
      });
    }
    // Keep the aria-label fresh as state changes via openQuickView /
    // closeQuickView mutating .is-quickview. MutationObserver is cheap
    // here — only fires on classList changes on the panel root.
    if (headerBackBtn) {
      const obs = new MutationObserver(() => {
        const inQv = root.classList.contains('is-quickview');
        headerBackBtn.setAttribute('aria-label', inQv ? 'Back to colours' : 'Close');
      });
      obs.observe(root, { attributes: true, attributeFilter: ['class'] });
    }

    document.addEventListener('keydown', (e) => {
      if (!detailPane || detailPane.hidden) return;
      if (e.key === 'ArrowLeft')  { e.preventDefault(); qvPrev?.click(); }
      if (e.key === 'ArrowRight') { e.preventDefault(); qvNext?.click(); }
    });

    // ---- Inline notice (auto-hide 3s) ──────────────────────────────────────
    // Used by the tier-down trim flow to surface "N colours removed". Single
    // timer per modal so a fresh notice cancels any pending hide.
    let _noticeTimer = null;
    function showNotice(message, ms = 3000) {
      if (!noticeEl) return;
      noticeEl.textContent = message;
      noticeEl.hidden = false;
      if (_noticeTimer) clearTimeout(_noticeTimer);
      _noticeTimer = setTimeout(() => {
        noticeEl.hidden = true;
        noticeEl.textContent = '';
        _noticeTimer = null;
      }, ms);
    }

    // ---- Click-to-pin tier (hybrid auto-derive + manual pin) ───────────────
    // Each tier circle is a <button data-gd-kit-tier data-tier-idx="N">.
    // Click sets state.pinnedTierIdx; render flips .is-active to that tier.
    // Pin clears automatically when adding a colour overflows the pinned
    // tier's slot_count (auto-bump in toggleTileSelection).
    //
    // Tier-down trim (per Joel 2026-04-28): clicking a smaller tier than
    // currently has colours selected closes a pricing exploit (pick 4 on
    // €134, click back to €114, get 2 colours free). Trim state.selected
    // LIFO down to the new slot_count, emit kit_color_removed for each
    // dropped colour, and surface an inline notice. The cart payload
    // reflects the trimmed selection (handleAtc uses live state.selected).
    const tierEls = [...root.querySelectorAll('[data-gd-kit-tier]')];
    tierEls.forEach((el) => {
      el.addEventListener('click', (e) => {
        e.preventDefault();
        const idx = parseInt(el.dataset.tierIdx, 10);
        if (Number.isNaN(idx) || idx < 0 || idx >= sortedTiers.length) return;
        const newTier = sortedTiers[idx];
        const newLimit = newTier.slot_count | 0;
        // Tier-down trim: only triggers when the new tier is SMALLER than
        // the current selection. Same-tier or larger-tier clicks no-op
        // here (selection stays whole, just the pinned tier flips).
        if (newLimit > 0 && state.selected.length > newLimit) {
          const dropped = state.selected.splice(newLimit);  // LIFO
          dropped.forEach((c, i) => emit('kit_color_removed', { variant_id: c.variantId, slot: newLimit + i, reason: 'tier_down_trim' }));
          const noun = dropped.length === 1 ? 'colour' : 'colours';
          showNotice(`${dropped.length} ${noun} removed to match this kit`);
        }
        state.pinnedTierIdx = idx;
        emit('kit_tier_pinned', { tier_idx: idx, slot_count: newLimit });
        render();
      });
    });

    // ---- Slot click → open colour preview (per Joel 2026-04-28) ────────────
    // Was: × button on each slot → one-tap destructive remove. Misclick risk
    // too high. New flow: tap slot → opens detail pane previewing that
    // colour. Removal happens via the detail-pane CTA ("Remove from box"),
    // a deliberate two-step interaction. The CTA already toggles its label
    // + style based on whether the colour is in state.selected (existing
    // .is-remove class behaviour from the in-place pane swap).
    slotsHosts.forEach((host) => {
      host?.addEventListener('click', (e) => {
        const slotBtn = e.target.closest('[data-gd-kit-slot][data-filled="true"]');
        if (!slotBtn) return;
        e.preventDefault();
        const idx = parseInt(slotBtn.dataset.slotIdx, 10);
        const colour = state.selected[idx];
        if (!colour) return;
        // Find original tile by variantId so openQuickView has data-images,
        // data-finish, etc. Tile may be filtered hidden by chips — that
        // doesn't matter; we read from dataset, not from visibility.
        const tile = tiles.find(t => parseInt(t.dataset.variantId, 10) === colour.variantId);
        if (tile) openQuickView(tile);
      });
    });

    // ---- Filter chips (single-select per Joel 2026-04-28) -------------------
    // Click any chip → all others deactivate, clicked chip becomes the only
    // active filter. Clicking the already-active chip is a no-op (prevents
    // a zero-chip state — the grid must always have a category to filter on).
    // Was multi-select union with last-chip guard (commit 0459f69 era);
    // dropped because Joel's reference UX is single-select.
    chips.forEach((chip) => {
      chip.addEventListener('click', () => {
        const gi = parseInt(chip.dataset.groupIndex, 10);
        if (state.chips.has(gi) && state.chips.size === 1) return;
        // Joel 2026-04-29: if the detail carousel is open, close it
        // before applying the new filter — otherwise the user sees the
        // grid filter behind the still-open detail pane and has to
        // tap BACK manually. Single gesture: pill tap → grid + filtered.
        if (detailPane && !detailPane.hidden) {
          closeQuickView();
        }
        state.chips.clear();
        state.chips.add(gi);
        applyChipStates();
        applyTileVisibility();
        // Quickview rebuilds on next openQuickView from the clicked
        // tile's data-images, so no separate cache invalidation is
        // needed (the per-shade rewrite removed _qvTrackKey).
        emit('kit_filter_toggled', { active: [...state.chips] });
      });
    });
    function applyChipStates() {
      chips.forEach((chip) => {
        const gi = parseInt(chip.dataset.groupIndex, 10);
        const isActive = state.chips.has(gi);
        chip.classList.toggle('is-active', isActive);
        chip.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      });
    }

    // Chips horizontal scroll arrows (desktop). [hidden] toggling drives
    // both visibility AND keyboard reachability — when there's no overflow
    // (or we're at the start/end edge) the arrow is removed from the tab
    // order entirely. Click scrolls 80% of the strip's visible width so the
    // user cycles through categories without micro-clicks.
    function updateChipsArrows() {
      if (!chipsScroll) return;
      const overflow = chipsScroll.scrollWidth - chipsScroll.clientWidth > 4;
      if (!overflow) {
        if (chipsPrev) chipsPrev.hidden = true;
        if (chipsNext) chipsNext.hidden = true;
        chipsScroll.classList.remove('has-fade-left', 'has-fade-right');
        return;
      }
      const max = chipsScroll.scrollWidth - chipsScroll.clientWidth;
      const atStart = chipsScroll.scrollLeft <= 2;
      const atEnd   = chipsScroll.scrollLeft >= max - 2;
      if (chipsPrev) chipsPrev.hidden = atStart;
      if (chipsNext) chipsNext.hidden = atEnd;
      // Joel 2026-04-29: edge-fade masks toggle in sync with the
      // arrows — left fade only when scrolled past start, right fade
      // only when there's content right of the visible area. Both
      // mid-scroll. Mask-image rules in kit-builder.css respond to
      // these classes.
      chipsScroll.classList.toggle('has-fade-left',  !atStart);
      chipsScroll.classList.toggle('has-fade-right', !atEnd);
    }
    if (chipsPrev) chipsPrev.addEventListener('click', () => {
      if (!chipsScroll) return;
      chipsScroll.scrollBy({ left: -chipsScroll.clientWidth * 0.8, behavior: 'smooth' });
    });
    if (chipsNext) chipsNext.addEventListener('click', () => {
      if (!chipsScroll) return;
      chipsScroll.scrollBy({ left:  chipsScroll.clientWidth * 0.8, behavior: 'smooth' });
    });
    if (chipsScroll) {
      chipsScroll.addEventListener('scroll', () => window.requestAnimationFrame(updateChipsArrows), { passive: true });
    }
    window.addEventListener('resize', updateChipsArrows);
    // Joel 2026-04-29: right chevron must be visible from initial render
    // when there's overflow — was previously gated on the user actually
    // scrolling. The modal mounts display:none, so the first measurement
    // returns 0/0 and both arrows hide. Solution: recompute on the
    // open-modal event, AND watch the chips strip with a ResizeObserver
    // so chip text reflows / font swaps / viewport resizes refresh the
    // visibility deterministically.
    if ('ResizeObserver' in window && chipsScroll) {
      const ro = new ResizeObserver(() => window.requestAnimationFrame(updateChipsArrows));
      ro.observe(chipsScroll);
    }
    document.addEventListener('gd-kit-modal:opened', () => {
      // rAF + setTimeout double-pass — modal flip from display:none to
      // visible needs one frame for layout, one more for sub-pixel.
      window.requestAnimationFrame(updateChipsArrows);
      setTimeout(updateChipsArrows, 80);
    });
    updateChipsArrows();
    window.requestAnimationFrame(updateChipsArrows);
    setTimeout(updateChipsArrows, 400);
    function applyTileVisibility() {
      tiles.forEach((t) => {
        const visible = state.chips.has(parseInt(t.dataset.groupIndex, 10));
        t.hidden = !visible;
      });
    }

    // ---- ATC ----------------------------------------------------------------
    atcButtons.forEach((btn) => btn.addEventListener('click', handleAtc));
    async function handleAtc() {
      // Fixed-tier gate (Joel 2026-04-29): ATC requires the active tier
      // to be FULLY filled (sel.length === slot_count). Belt-and-braces
      // against any path that bypasses the disabled attribute (keyboard
      // Enter on an aria-disabled button, racey state during the trim
      // path on tier-down, etc.). Empty + no-active-tier guard kept.
      const active = getActiveTier();
      if (!active) return;
      const required = active.slot_count | 0;
      if (required <= 0 || state.selected.length !== required) return;
      // Joel 2026-04-30: orchestrated handoff. Spinner replaces text
      // immediately so the click feels registered. Width is preserved
      // by toggling a class (see kit-builder.css .is-loading rule); no
      // layout shift between text and spinner.
      atcButtons.forEach(b => { b.disabled = true; b.classList.add('is-loading'); b.setAttribute('aria-busy', 'true'); });
      if (errorEl) errorEl.hidden = true;

      const bundleId = uuid();
      const coloursStr = state.selected.map(c => c.name).join(',');
      const items = [
        {
          // Parent variant = the active tier's variant_id (resolved by
          // Liquid via variant_match → product.variants lookup, falls back
          // to default_variant.id when no match).
          id: parseInt(active.variant_id, 10) || defaultVariantId,
          quantity: 1,
          properties: {
            Tier:         active.title || active.variant_match || '',
            Colours:      coloursStr,
            __bundle_id:  bundleId,
            __slot_count: String(active.slot_count),
            // _kit flag (single-underscore = hidden from default Shopify
            // line-item-property UI). Cart drawer + cart page group by
            // __bundle_id and render parent + nested children when this
            // flag is set.
            _kit:         'true',
          },
        },
        ...state.selected.map(c => ({
          id: c.variantId,
          quantity: 1,
          // _addon flag (single-underscore = hidden). Children render
          // nested under parent in the cart UI; never editable directly
          // (no qty stepper, no remove button — parent controls them all).
          properties: { __bundle_id: bundleId, _addon: 'true' },
        })),
      ];
      emit('kit_atc_click', { count: state.selected.length, tier_slot_count: active.slot_count, tier_price_cents: active.price_cents });

      try {
        const r = await fetch('/cart/add.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ items }),
        });
        if (!r.ok) {
          const body = await r.json().catch(() => ({}));
          throw new Error(body.description || 'Cart add failed');
        }
        emit('kit_atc_success', { count: state.selected.length, tier_price_cents: active.price_cents });
        // Klaviyo "Added to Cart" — bundle-level: ONE event for the whole
        // kit so $value aggregates correctly. Bundle composition exposed
        // as custom keys for segmentation.
        try {
          if (window._learnq && typeof window._learnq.push === 'function') {
            const tierPriceDollars = (active.price_cents || 0) / 100;
            window._learnq.push(['track', 'Added to Cart', {
              $value: tierPriceDollars,
              AddedItemProductName: active.title || 'Salon Kit',
              AddedItemProductID:   active.variant_id ? String(active.variant_id) : '',
              AddedItemQuantity:    1,
              AddedItemPrice:       tierPriceDollars,
              AddedItemURL:         window.location.origin + window.location.pathname,
              AddedItemImageURL:    active.image || (active.featured_image && active.featured_image.url) || '',
              AddedItemCategories:  ['Salon Kit'],
              BundleTier:           active.title || active.variant_match || '',
              BundleColours:        state.selected.map(c => c.name).filter(Boolean),
              BundleSlotCount:      active.slot_count,
            }]);
          }
        } catch (e) {}
        // Joel 2026-04-30 (orchestrated handoff w/ optional upsell):
        // 1. Flash a check on the CTA so the click reads as confirmed.
        // 2. Hand off to the upsell module (window.gdUpsell.maybeShow).
        //    It returns a Promise that resolves once the user has
        //    accepted / declined / closed — or resolves immediately
        //    with { accepted:false } when the upsell is short-circuited
        //    (disabled, frequency-capped, OOS, B2B, already-in-cart).
        // 3. AFTER the upsell promise resolves, open the cart drawer
        //    and close the bundle modal — same close animation as
        //    before. The upsell mounts INSIDE the bundle panel so we
        //    don't trigger the modal's exit animation until step 3.
        atcButtons.forEach(b => { b.classList.remove('is-loading'); b.classList.add('is-success'); });
        // Joel 2026-04-30 (regression fix): dispatch `cart:updated` (refreshes
        // the count badge) instead of `product-added-to-cart` (which ALSO
        // auto-opens the cart drawer in gd-cart-drawer.js — covering the
        // upsell modal that's about to mount inside the bundle panel).
        // The drawer opens later via the `gd:cart-open` dispatch in
        // finishHandoff(), AFTER the upsell promise resolves. This way:
        //   - count badge updates immediately ✓
        //   - cart drawer waits for upsell to accept/decline/close ✓
        //   - non-bundle ATC paths (single-product PDPs) still get the
        //     drawer-on-add behaviour because they go through gd-cart-add.js
        //     which dispatches both events itself.
        document.body.dispatchEvent(new CustomEvent('cart:updated'));

        const finishHandoff = () => {
          // Joel 2026-05-02 (round 6 fix): dispatch `product-added-to-cart`
          // (refresh-then-open) instead of `gd:cart-open` (open-only). The
          // drawer's `gd:cart-open` listener calls open(), and open() only
          // refreshes /cart.js on FIRST hydration — every subsequent open
          // shows whatever cart state the drawer was last rendered with.
          // After a kit ATC, that stale state is most often "empty cart"
          // (when the user opened the header cart icon before adding the
          // kit) or the prior cart contents. The `product-added-to-cart`
          // listener does `await refresh(); open();` so the drawer
          // always reflects the latest /cart.js after a kit add. This
          // PRESERVES the late-open architecture (still happens AFTER
          // upsell resolves, drawer doesn't cover the upsell modal),
          // it just fixes the staleness on the open itself.
          document.body.dispatchEvent(new CustomEvent('product-added-to-cart'));
          if (modal) modal.classList.add('is-closing');
          // Joel 2026-04-30 (scroll-lock chain fix): guard against onEnd
          // firing twice. The fallback setTimeout used to ALSO call
          // onEnd even after animationend already did, so closeModal()
          // (and its scroll-lock unlock) ran twice. Net effect: body
          // unlocked one count too far, exposing the page underneath
          // the now-open cart drawer to a scroll position jump. The
          // `done` flag makes onEnd idempotent.
          let done = false;
          const onEnd = () => {
            if (done) return;
            done = true;
            modal && modal.removeEventListener('animationend', onEnd);
            state.selected.length = 0;
            render();
            atcButtons.forEach(b => {
              b.classList.remove('is-success');
              b.removeAttribute('aria-busy');
              b.disabled = false;
            });
            closeModal();
            modal && modal.classList.remove('is-closing');
          };
          if (modal) {
            modal.addEventListener('animationend', onEnd, { once: true });
            setTimeout(onEnd, 350);
          } else {
            onEnd();
          }
        };

        // ~120ms head start on the CTA check before either the upsell
        // crossfade-in or the cart-drawer slide-in begins.
        setTimeout(async () => {
          const bundleData = { bundle_id: bundleId, count: state.selected.length, tier_price_cents: active.price_cents };
          // Observability: emit a trigger event on every bundle-add so
          // we can see in DevTools / analytics whether kit-builder
          // actually called maybeShow. If you see this event but no
          // upsell modal, the issue is in the modal's guards (sessionStorage
          // cap, already-in-cart, B2B suppression). If you DON'T see this
          // event, the issue is in kit-builder — call site missing.
          console.log('[upsell-trigger] bundle added, calling maybeShow', bundleData);
          try {
            document.dispatchEvent(new CustomEvent('gd:event', { detail: { name: 'upsell_trigger_called', payload: bundleData } }));
            if (window.dataLayer && window.dataLayer.push) window.dataLayer.push({ event: 'upsell_trigger_called', ...bundleData });
          } catch (e) {}
          const upsell = (window.gdUpsell && typeof window.gdUpsell.maybeShow === 'function')
            ? window.gdUpsell
            : null;
          if (!upsell) {
            console.warn('[upsell-trigger] window.gdUpsell missing — modal section not loaded on this page');
          }
          if (upsell) {
            try {
              await upsell.maybeShow(bundleData);
            } catch (err) {
              console.warn('[kit-builder] upsell flow errored, falling through to cart drawer:', err);
            }
          }
          finishHandoff();
        }, 120);
      } catch (e) {
        emit('kit_atc_error', { message: e.message });
        if (errorEl) { errorEl.textContent = e.message; errorEl.hidden = false; }
        atcButtons.forEach(b => {
          b.classList.remove('is-loading');
          b.classList.remove('is-success');
          b.removeAttribute('aria-busy');
          b.disabled = false;
        });
      }
    }

    // ---- Render -------------------------------------------------------------
    function render() {
      const sel      = state.selected;
      const active   = getActiveTier();
      const activeIdx = active ? sortedTiers.indexOf(active) : -1;
      const activeSlots = active ? (active.slot_count|0) : 0;

      // Stepper state — single-active per Joel 2026-04-28 fix.
      // .is-active: only the current active tier (filled purple + check).
      // .is-reached: tiers STRICTLY below active (used by connector CSS to
      //              fill the line segments leading up to active; does NOT
      //              fill the dot — that's active-only).
      root.querySelectorAll('[data-gd-kit-tier]').forEach((el, idx) => {
        const isActive  = idx === activeIdx;
        const isReached = activeIdx > -1 && idx < activeIdx;
        el.classList.toggle('is-reached', isReached);
        el.classList.toggle('is-active',  isActive);
        el.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      });

      // Summary slots (visible row count) = active tier's slot_count, but
      // never less than current selection length (defensive — handles the
      // edge case where user pinned a smaller tier than their selection).
      let summarySlots = activeSlots;
      if (summarySlots < sel.length) summarySlots = sel.length;
      if (summarySlots === 0) summarySlots = sortedTiers[0]?.slot_count || 0;

      // Slot rows (desktop summary + mobile footer share rendering logic).
      // Filled slots render as <button> with an info-icon overlay (i) — tap
      // opens the colour detail preview. Removal becomes deliberate via the
      // detail-pane CTA ("Remove from box") — closes Joel 2026-04-28's
      // accidental-misclick concern. Empty slots render as <div> with a
      // numbered placeholder (Bolt-faithful, not interactive).
      slotsHosts.forEach((host) => {
        if (!host) return;
        host.innerHTML = '';
        for (let i = 0; i < summarySlots; i++) {
          const c = sel[i];
          const slot = document.createElement(c ? 'button' : 'div');
          slot.className = 'gd-kb__slot';
          slot.dataset.gdKitSlot = '';
          slot.dataset.slotIdx = String(i);
          slot.setAttribute('role', 'listitem');
          if (c) {
            slot.type = 'button';
            slot.setAttribute('aria-label', `Preview ${c.name}`);
            slot.setAttribute('data-filled', 'true');
            // Pop animation only on the slot that was just filled by the
            // most recent toggleTileSelection. Drops back to default on
            // every other render. The CSS @keyframes finishes in ~280ms.
            if (i === _justFilledSlotIdx) slot.classList.add('is-just-filled');
            if (c.image) {
              const img = document.createElement('img');
              img.src = c.image; img.alt = c.name; img.loading = 'lazy';
              img.className = 'gd-kb__slot-img';
              slot.appendChild(img);
            } else if (c.swatch) {
              slot.style.background = c.swatch;
            }
            const info = document.createElement('span');
            info.className = 'gd-kb__slot-info';
            info.setAttribute('aria-hidden', 'true');
            info.innerHTML = '<svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>';
            slot.appendChild(info);
          } else {
            slot.setAttribute('aria-label', `Slot ${i + 1}: empty`);
            // Bolt-faithful empty slot: numbered placeholder (i+1).
            const num = document.createElement('span');
            num.className = 'gd-kb__slot-num';
            num.textContent = String(i + 1);
            slot.appendChild(num);
          }
          host.appendChild(slot);
        }
      });

      // No count text — slot fill state is the visual count. Both the
      // picker subhead counter (compact pass) and the footer "0 / N"
      // tally (round 2) are gone; the price inline at the top-right of
      // the footer head is the only metric in that row.

      // Price stack — active tier's price comes from product.variants[matched]
      // .price (Liquid pre-formats with money_without_trailing_zeros into
      // price_str so JS doesn't hardcode the currency symbol; falls back to
      // a JS format if Liquid didn't emit a string for some reason).
      if (active) {
        const priceStr = active.price_str || fmtCents(active.price_cents);
        priceEls.forEach(el => el.textContent = priceStr);
      } else {
        priceEls.forEach(el => el.textContent = '');
      }
      // Joel 2026-05-02: populate the .gd-kb__was strikethrough + savings
      // when the active tier has compare_at_cents > price_cents. Tier
      // JSON now emits compare_at_str + savings_str pre-formatted by
      // Liquid's money_without_trailing_zeros (so JS doesn't have to
      // handle currency symbols). When no compare-at, both clear and
      // hide as before.
      if (active && active.compare_at_cents > active.price_cents) {
        const wasStr = active.compare_at_str || fmtCents(active.compare_at_cents);
        wasEls.forEach((el) => {
          el.textContent = wasStr;
          el.style.display = '';
        });
        if (savingsEl) {
          const savingsStr = active.savings_str || fmtCents(active.savings_cents);
          savingsEl.textContent = 'Save ' + savingsStr;
          savingsEl.style.display = '';
        }
      } else {
        wasEls.forEach((el) => { el.textContent = ''; el.style.display = 'none'; });
        if (savingsEl) { savingsEl.textContent = ''; savingsEl.style.display = 'none'; }
      }

      // Value caption (Joel 2026-04-29 v2) — flat caption under the
      // price reads "{count} · {cost}" with the count lowercased so
      // it pairs with caption styling rather than upper-cased pill
      // text. Per-tier values come from data-tier-N-count / -cost on
      // the parent .gd-kb__price-block; JS reads them on every
      // render and swaps text. .is-updated runs a translate/opacity
      // pop on actual tier changes (skipped on first paint).
      const slot = active ? (active.slot_count | 0) : 0;
      if (priceBlockEls.length && slot > 0) {
        const tierChanged = slot !== _lastChipSlotCount;
        priceBlockEls.forEach((block) => {
          const caption = block.querySelector('[data-gd-value-caption]');
          if (!caption) return;
          const count = block.getAttribute('data-tier-' + slot + '-count') || '';
          const cost  = block.getAttribute('data-tier-' + slot + '-cost')  || '';
          const countEl = caption.querySelector('[data-gd-value-count]');
          const costEl  = caption.querySelector('[data-gd-value-cost]');
          if (countEl) countEl.textContent = count.toLowerCase();
          if (costEl)  costEl.textContent  = cost;
          caption.hidden = !(count || cost);
          if (tierChanged && _lastChipSlotCount !== -1 && (count || cost)) {
            caption.classList.remove('is-updated');
            void caption.offsetWidth;  // force reflow → restart animation
            caption.classList.add('is-updated');
            setTimeout(() => caption.classList.remove('is-updated'), 320);
          }
        });
        _lastChipSlotCount = slot;
      }

      // Progress bar (legacy element, only present in some layouts)
      if (progressFillEl) {
        const pct = summarySlots > 0 ? Math.min((sel.length / summarySlots) * 100, 100) : 0;
        progressFillEl.style.width = pct.toFixed(1) + '%';
      }

      // Upsell line removed (Joel 2026-04-28). Empty out if the element
      // somehow still exists in the DOM (defensive — markup was deleted).
      if (upsellEl) upsellEl.textContent = '';

      // Status — desktop summary panel only (mobile dropped the line).
      if (statusEl) {
        if (sel.length === 0)              statusEl.textContent = '';
        else if (active && sel.length === activeSlots) statusEl.textContent = 'Package complete.';
        else                               statusEl.textContent = `${sel.length} of ${activeSlots} selected.`;
      }

      // Tile selected ring (direct-toggle visual)
      const selectedIds = new Set(sel.map(c => c.variantId));
      tiles.forEach(t => {
        const isSel = selectedIds.has(parseInt(t.dataset.variantId, 10));
        if (isSel) t.setAttribute('data-selected', 'true');
        else t.removeAttribute('data-selected');
      });

      // ATC validation (Joel 2026-04-29): enabled ONLY when the active
      // tier's slot count is fully filled. Was `sel.length >= 1` —
      // produced an enabled CTA on underfilled bundles (e.g. 1 of 2
      // selected on the 2-colour tier still let the user add a half-
      // empty bundle). Now requires sel.length === required.
      const required  = active ? (active.slot_count|0) : 0;
      const remaining = Math.max(0, required - sel.length);
      const ready     = required > 0 && sel.length === required;
      atcButtons.forEach(b => {
        b.disabled = !ready;
        b.setAttribute('aria-disabled', ready ? 'false' : 'true');
        if (ready) b.removeAttribute('title');
        else b.setAttribute('title', 'Select all colours to continue');
      });
      atcTextEls.forEach(el => {
        if (ready)                  el.textContent = 'Add bundle to cart';
        else if (sel.length === 0)  el.textContent = 'Pick colours to continue';
        else                        el.textContent = `Pick ${remaining} more colour${remaining === 1 ? '' : 's'}`;
      });

      // Clear the just-filled marker so the pop animation only plays
      // once per selection (subsequent renders won't re-trigger it).
      _justFilledSlotIdx = -1;
    }

    // ---- Helpers ------------------------------------------------------------
    function safeJson(s, fb) { try { return JSON.parse(s || '[]'); } catch { return fb; } }
    function fmtCents(c) {
      const currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'AUD';
      return new Intl.NumberFormat('en-AU', {
        style: 'currency',
        currency,
        minimumFractionDigits: c % 100 === 0 ? 0 : 2,
        maximumFractionDigits: 2,
      }).format(c / 100);
    }
    function uuid() { return crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2) + Date.now().toString(36); }
    function emit(name, detail) {
      document.body.dispatchEvent(new CustomEvent(name, { detail }));
      if (window.dataLayer) window.dataLayer.push({ event: name, ...detail });
    }

    // ---- Initial paint ------------------------------------------------------
    // No pre-pinned tier — getActiveTier() returns the first tier when the
    // selection is empty, so the stepper renders with tier 1 active on load.
    applyChipStates();
    applyTileVisibility();
    render();

    emit('kit_page_view', { product_id: productId, product_handle: handle });
  }
})();
